/**
 * NextStepsCard.tsx — the deal page's Next steps card (deal detail slice 4,
 * §39.9 order 4; spec §3.4 B). It replaces the Tasks card and keeps all it
 * did: add (now with a due day), tick, and reopen a done task. The rules are
 * in nextSteps.ts; the writes are the page's (the existing activities POST and
 * the complete / reopen routes), so the timeline sees every task the moment
 * the server has it.
 *
 * "Today" is the server's clock on London calendar days (R1; lib/serverClock,
 * workingDays.ts) — never the browser's date.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import { Check, Plus, Sparkles } from 'lucide-react'
import type { SalesActivity } from '../../../../api/types'
import type { DealDetail } from '../../types'
import { accent, info, surface } from '../../../../theme/tokens'
import { serverNow } from '../../../../lib/serverClock'
import { londonDay, noonLondon } from './workingDays'
import { isClosedStage } from './dealEdit'
import { useWriteAccess } from '../../../../lib/writeAccess'
import {
  suggestionsFor, openTaskLabel, taskMeta, isTaskDone, sortTasks, chipDay,
  DUE_CHOICES, resolveDueChoice, type DueChoice,
} from './nextSteps'

export function NextStepsCard({ deal, tasks, contactName, loading, failed, onCreate, onToggle }: {
  deal: DealDetail
  /** The deal's activities of type 'task' (any status). */
  tasks: readonly SalesActivity[]
  /** The linked contact's name; '' while it loads or when it cannot be read;
   *  null when the deal has no linked contact. */
  contactName: string | null
  loading: boolean
  failed: boolean
  /** Resolves true once the server has the task. */
  onCreate: (subject: string, dueAt?: string) => Promise<boolean>
  onToggle: (t: SalesActivity) => void
}) {
  const [title, setTitle] = useState('')
  const [due, setDue] = useState<DueChoice>('next')
  const [picked, setPicked] = useState('')
  const [busy, setBusy] = useState(false)
  // Task create / complete / reopen are records-level writes (lib/writeAccess):
  // without access the list reads only — a done mark, no checkbox, no add row,
  // no suggestion chips.
  const { records } = useWriteAccess()

  const today = londonDay(serverNow())
  const suggestions = isClosedStage(deal.stage) ? [] : suggestionsFor(deal.stage, contactName === null ? null : { name: contactName }, today, tasks)
  const sorted = sortTasks(tasks)
  const hasOpen = tasks.some(t => !isTaskDone(t))
  const dueDay = resolveDueChoice(due, today, picked)
  const canAdd = Boolean(title.trim()) && !(due === 'pick' && !dueDay) && !busy

  async function create(subject: string, day: string) {
    setBusy(true)
    try {
      return await onCreate(subject, day ? noonLondon(day) : undefined)
    } finally {
      setBusy(false)
    }
  }

  async function addManual() {
    if (!canAdd) return
    if (await create(title.trim(), dueDay)) {
      setTitle('')
      setPicked('')
    }
  }

  const inputCls = 'h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent'

  return (
    <div className="k-card" data-next-steps>
      <div className="k-card-head">
        <div>
          <div className="k-card-title">Next steps</div>
          {failed && <div className="k-card-sub">Tasks could not be loaded</div>}
        </div>
        {!loading && !failed && <div className="text-12 text-ink-muted">{openTaskLabel(tasks)}</div>}
      </div>
      <div className="p-5 flex flex-col gap-3">
        {!loading && !failed && !hasOpen && (
          <div className="text-13 text-ink-muted">No next step planned. Deals without one are the first to stall.</div>
        )}
        {sorted.length > 0 && (
          <ul className="flex flex-col gap-3" aria-label="Tasks on this deal">
            {sorted.map(t => {
              const done = isTaskDone(t)
              const meta = taskMeta(t)
              return (
                <li key={t.id} className="flex items-start gap-3">
                  {records ? (
                  <button type="button" role="checkbox" aria-checked={done} aria-label={t.subject} onClick={() => onToggle(t)}
                    className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center border transition-colors flex-shrink-0 ${done ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control hover:border-ink-disabled'}`}
                    style={done ? { background: accent.DEFAULT } : {}}>
                    {done && <Check size={12} />}
                  </button>
                  ) : (
                  <span aria-hidden="true"
                    className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center border flex-shrink-0 ${done ? 'border-transparent text-ink-on-accent' : 'border-edge-control bg-surface-control'}`}
                    style={done ? { background: accent.DEFAULT } : {}}>
                    {done && <Check size={12} />}
                  </span>
                  )}
                  <div className="min-w-0">
                    <div className={`text-14 font-medium ${done ? 'text-ink-muted line-through' : 'text-ink-primary'}`}>{t.subject}</div>
                    {meta && <div className="text-12 text-ink-muted mt-0.5">{meta}</div>}
                  </div>
                </li>
              )
            })}
          </ul>
        )}

        {records && suggestions.length > 0 && (
          <div className="rounded-[12px] p-3 flex flex-col gap-2" style={{ background: info.bg }} data-next-step-suggestions>
            <div className="text-12 font-semibold text-ink-secondary inline-flex items-center gap-1.5">
              <Sparkles size={12} aria-hidden="true" style={{ color: info.text }} />
              Suggested for a {deal.stage} deal
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map(s => (
                <button key={s.title} type="button" disabled={busy} onClick={() => void create(s.title, s.due)}
                  className="inline-flex items-center px-3 py-1 rounded-full text-12 font-medium border border-edge-control disabled:opacity-50"
                  style={{ background: surface.card, color: info.text }}>
                  + {s.title} · {chipDay(s.due)}
                </button>
              ))}
            </div>
            <div className="text-12 text-ink-muted">Due dates are the next working days, skipping weekends and UK bank holidays.</div>
          </div>
        )}

        {records && <div className="flex items-center gap-2 pt-1 flex-wrap">
          <input value={title} onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void addManual() }}
            placeholder="Add a next step…" aria-label="Next step" className={`${inputCls} flex-1 min-w-[160px]`} />
          <select aria-label="Due" value={due} onChange={e => setDue(e.target.value as DueChoice)} className={inputCls}>
            {DUE_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {due === 'pick' && (
            <input type="date" aria-label="Due day" value={picked} min={today} onChange={e => setPicked(e.target.value)} className={inputCls} />
          )}
          <button type="button" onClick={() => void addManual()} disabled={!canAdd} aria-label="Add next step"
            className="k-btn-secondary k-btn-secondary--flex disabled:opacity-50">
            <Plus size={13} />
          </button>
        </div>}
      </div>
    </div>
  )
}
